import { gql, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import useDate, {
  IDataType as IDateDataType,
  IOptionType as IDateOptionType,
} from '../common/date'
import useAllStores, {
  IDataType as IAllStoresDataType,
} from '../common/useAllStoresV2'

export interface IOptionType {
  dateOptions?: IDateOptionType
}

export interface IDataType extends IDateDataType, IAllStoresDataType {
  allStoreColumns?: {
    key: string
    title: string
  }[]
  bbbPurchaseCategories?: {
    loading: boolean
    disable: boolean
    value: string[][]
    onChange: (value: string[][]) => void
    options: {
      id: string
      parentId: string
      displayName: string
    }[]
  }
  bbbInventoryCategories?: {
    loading: boolean
    disable: boolean
    value: string[][]
    onChange: (value: string[][]) => void
    options: {
      id: string
      parentId: string
      displayName: string
    }[]
  }
}

interface IPurchaseSummaryType {
  purchase: {
    nodes: {
      reportResult: {
        tableData:
          | {
              itemDetails: Record<
                string,
                {
                  type: 'all' | 'item' | 'category'
                  item: string
                }
              >
            }[]
          | null
      }
    }[]
  }
  inventory: {
    nodes: {
      reportResult: {
        tableData:
          | {
              itemType?: string
            }[]
          | null
      }
    }[]
  }
}

const query = gql`
  query BbbCorporateItems($iInputParams: JSON!) {
    purchase: getCustomizedReport(
      iCustomizedReportName: "CORPORATE_PURCHASE_TABLE_V3"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }

    inventory: getCustomizedReport(
      iCustomizedReportName: "CORPORATE_INVENTORY_TABLE_V2"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

const ALL_CATEGORIES = 'All Categories'

const useBbbCorporateItems = ({ dateOptions = {} }: IOptionType) => {
  const { date } = useDate(dateOptions)
  const startDate = date?.value.dateRange.startDateStr
  const endDate = date?.value.dateRange.endDateStr
  const { allStores } = useAllStores({
    startDate,
    endDate,
  })

  const purchaseItemsHook = useState<string[][]>([])
  const inventoryItemsHook = useState<string[][]>([])

  const { data, loading: purchaseLoading } = useQuery<IPurchaseSummaryType>(
    query,
    {
      variables: {
        iInputParams: {
          locationGroupId: allStores?.locationGroupIds[0],
          locationGroupIds: allStores?.locationGroupIds,
          startDate,
          endDate,
          otherParams: {
            inventoryType: 'INVENTORY_WEEKLY',
          },
        },
      },
      skip: !startDate || !endDate || !allStores?.locationGroupIds,
    },
  )
  const loading = !allStores || purchaseLoading

  return {
    allStores,
    allStoreColumns: useMemo(
      (): IDataType['allStoreColumns'] =>
        !allStores
          ? undefined
          : Object.values(allStores.locations)
              .sort((a, b) => a.code - b.code)
              .map((l) => ({
                key: `locationDetails.${l.id}.variance`,
                title: `${l.code} - ${l.name}`,
              })),
      [allStores],
    ),

    bbbPurchaseCategories: useMemo((): IDataType['bbbPurchaseCategories'] => {
      const itemDetails =
        data?.purchase.nodes[0]?.reportResult.tableData?.[0]?.itemDetails

      return {
        loading,
        disable: !itemDetails && !loading,
        value: purchaseItemsHook[0],
        onChange: purchaseItemsHook[1],
        options: !itemDetails
          ? []
          : [
              {
                id: 'category',
                parentId: 'root' as const,
                displayName: 'Category',
              },
              {
                id: 'item',
                parentId: 'root' as const,
                displayName: 'Item',
              },
              ...Object.values(itemDetails).map((t) => ({
                id: t.item,
                parentId: t.type === 'all' ? 'root' : t.type,
                displayName: t.item,
              })),
            ].sort((a, b) => a.displayName.localeCompare(b.displayName)),
      }
    }, [purchaseItemsHook, data, loading]),

    bbbInventoryCategories: useMemo((): IDataType['bbbInventoryCategories'] => {
      const tableData = data?.inventory.nodes[0]?.reportResult.tableData

      return {
        loading,
        disable: !tableData && !loading,
        value: inventoryItemsHook[0],
        onChange: inventoryItemsHook[1],
        options: !tableData
          ? []
          : [
              {
                id: ALL_CATEGORIES,
                parentId: 'root' as const,
                displayName: ALL_CATEGORIES,
              },
              ...tableData
                .reduce(
                  (result, data) =>
                    !data.itemType || result.includes(data.itemType)
                      ? result
                      : [...result, data.itemType],
                  [] as string[],
                )
                .sort()
                .map((category) => ({
                  id: category,
                  parentId: 'root' as const,
                  displayName: category,
                })),
            ],
      }
    }, [inventoryItemsHook, data, loading]),
  }
}

export default useBbbCorporateItems
